import { LayoutService } from '@pf/shared-ui';
import { NavigationEnd, Router } from '@angular/router';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import {
    PlatformAuthService,
    ProfileProvider,
    sharedEnv,
    TenantProvider,
} from '@pf/shared-common';
import {
    combineLatest,
    filter,
    first,
    firstValueFrom,
    of,
    switchMap,
    tap,
} from 'rxjs';
import { PlatformRoutes } from '../routing/PlatformRoutes';
import { NzIconService } from 'ng-zorro-antd/icon';
import { NativeAppBarService } from '@pf/shared/ui-platform';

@Component({
    template: '',
    standalone: false,
})
export abstract class BaseAppComponent implements OnInit {
    private router = inject(Router);
    private cdr = inject(ChangeDetectorRef);
    private authService = inject(PlatformAuthService);
    private tenantProvider = inject(TenantProvider);
    private profileProvider = inject(ProfileProvider);
    private readonly nzIconService = inject(NzIconService);
    private readonly nativeAppBar = inject(NativeAppBarService);

    public layoutService = inject(LayoutService);

    title = 'Control Tower';
    appRouter: PlatformRoutes = {} as PlatformRoutes;
    fullPageLayout = false;
    private loaded = false;
    private loading = false;

    constructor() {
        this.listenForRouteChanges();
        this.nzIconService.changeAssetsSource(sharedEnv.assetsSource);
    }

    ngOnInit(): void {
        this.checkIfLoaded(window.location.pathname);
    }

    private loadAppData() {
        if (this.loading || this.loaded) {
            return;
        }
        this.loading = true;
        this.addLoader();
        this.fullPageLayout = false;
        this.authService.login();
        this.tenantProvider.load();
        this.profileProvider.load();

        return firstValueFrom(
            combineLatest([
                this.tenantProvider.tenants$,
                this.profileProvider.permissions$,
            ]).pipe(
                filter(tenants => Object.keys(tenants).length > 0),
                tap(() => {
                    this.removeLoader();
                    this.loaded = true;
                    this.loading = false;
                })
            )
        );
    }

    private addLoader() {
        document.body.classList.remove('loaded');
    }

    private removeLoader() {
        this.authService.isLoading$
            .pipe(
                filter(isLoading => !isLoading),
                first(),
                tap(() => {
                    document.body.classList.add('loaded');
                    this.cdr.markForCheck();
                }),
                switchMap(() =>
                    this.nativeAppBar.canUseNativeAppBar
                        ? this.nativeAppBar.toggleVisibility(false)
                        : of(false)
                )
            )
            .subscribe();
    }

    private checkIfLoaded(url: string) {
        if (url.startsWith('/public')) {
            if (url.indexOf('login-callback') > -1) {
                this.router.navigateByUrl('/');
                this.loadAppData();
            } else {
                this.fullPageLayout = true;
                this.removeLoader();
            }
        } else {
            if (!this.loaded) {
                this.loadAppData();
            }
            this.fullPageLayout = false;
        }
    }

    private listenForRouteChanges() {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.checkIfLoaded(event.url);
            }
        });
    }
}
